import { useMemo, useState } from 'react'
import { NetworkStatus } from '@apollo/client'
import {
	EuiButton,
	EuiFlexGroup,
	EuiFlexItem,
	EuiPageSection,
	EuiSpacer,
} from '@equipmentshare/ds2'

import GroupsList from './components/GroupsList'
import SearchBar from 'views/components/SearchBar'
import Pagination from 'views/components/Pagination'

import { useGroupsQuery } from '../../../../graphql'

import { useDispatchSetSelectedModal } from 'contexts/modal-context/modal-context'
import { getAddGroupModal, getEditGroupModal, getRemoveGroupModal } from './components/actionModals'
import { heapEvent } from '@equipmentshare/heaper'
import { escapeRegExp } from 'views/helpers/utils'

export type Group = {
	id: number,
	name: string,
	spendingLimit?: number,
}

export default function Groups() {
	const { data: groupsData, loading, networkStatus, refetch } = useGroupsQuery( { notifyOnNetworkStatusChange: true } )
	const isFetchingGroups = useMemo( () => loading || networkStatus === NetworkStatus.refetch, [ loading, networkStatus ] )
	const setSelectedModal = useDispatchSetSelectedModal()
	const [ search, setSearch ] = useState( '' )
	const [ activePage, setActivePage ] = useState( 0 )
	const [ groupsPerPage, setGroupsPerPage ] = useState( 20 )

	const filteredGroups = useMemo( () => {
		if ( !groupsData ) {
			return []
		}

		const rawGroups = groupsData.groups as Group[]
		if ( search !== '' ) {
			const regexSearch = new RegExp( escapeRegExp( search ), 'i' )
			return rawGroups.filter( ( group ) => regexSearch.exec( group.name ) )
		}

		return rawGroups
	}, [ groupsData, search ] )


	const pageCount = useMemo( () => {
		if ( filteredGroups ) {
			return Math.ceil( filteredGroups.length / groupsPerPage )
		}
		return 0
	}, [ filteredGroups, groupsPerPage ] )

	const groupsOnPage = useMemo( () => {
		if ( filteredGroups.length > 0 ) {
			const start = activePage * groupsPerPage
			const end = ( activePage + 1 ) * groupsPerPage

			return filteredGroups.slice( start, end )
		}
		return []
	}, [ filteredGroups, activePage, groupsPerPage ] )


	const onPageClick = ( page: number ) => setActivePage( page )
	const onSearch = ( newSearch: string ) => {
		setSearch( newSearch )
		setActivePage( 0 )
	}

	const onAddGroupClick = () => {
		heapEvent( 'Inventory - Permission Groups - Click - Add Group', {} )
		setSelectedModal( getAddGroupModal( refetch ) )
	}

	const onRemoveGroupClick = ( group: Group ) => () => {
		heapEvent( 'Inventory - Permission Groups - Click - Delete Group', {} )
		setSelectedModal( getRemoveGroupModal( group, refetch ) )
	}

	const onEditGroupClick = ( group: Group ) => () => {
		heapEvent( 'Inventory - Permission Groups - Click - Edit', {} )
		setSelectedModal( getEditGroupModal( group, onRemoveGroupClick( group ) ) )
	}

	const fullHeight = 'eui-fullHeight'

	return (
		<EuiPageSection className={fullHeight} paddingSize="none">
			<EuiSpacer />
			<EuiFlexGroup>
				<EuiFlexItem>
					<SearchBar
						search={search}
						setSearch={onSearch}
						placeholder="Search group names"
					/>
				</EuiFlexItem>
				<EuiFlexItem>
					<EuiFlexGroup
						justifyContent="flexEnd"
						gutterSize="m"
					>
						<EuiFlexItem grow={false}>
							<EuiButton
								id="addBtn"
								color="text"
								iconType="plus"
								onClick={onAddGroupClick}
							>
								Add group
							</EuiButton>
						</EuiFlexItem>
					</EuiFlexGroup>
				</EuiFlexItem>
			</EuiFlexGroup>

			<EuiSpacer size="xxl" />
			<GroupsList
				groups={groupsOnPage}
				isLoading={isFetchingGroups}
				onEditGroup={onEditGroupClick}
			/>
			<Pagination
				pageCount={pageCount}
				activePage={activePage}
				onPageClick={onPageClick}
				itemsPerPage={groupsPerPage}
				setItemsPerPage={setGroupsPerPage}
			/>
		</EuiPageSection>
	)
}
